/*******************************************************************************
 *
 * This file is part of the LGS1920/studio project.
 *  
 * File: style.css
 *
 * Author : LGS1920 Team
 * email: studio@lgs1920.fr
 *
 * Created on: 2026-06-18
 * Last modified: 2026-06-18
 *
 *
 * Copyright © 2026 LGS1920
 ******************************************************************************/

:root {
    --lgs-widget-editing-bg-color: var(--lgs-third-color-75);
    --lgs-widget-editing-color: var(--lgs-second-color-75);
    --dashing-width: 25px;
    --outline-width: 2px;
    --label-border-dance-duration: 40s;
}

/**
 * Animated outline using a pseudo-element.
 * It is positioned outside the actual border.
 */
.text-editing-progress {
    position: relative;
    /* Ensure the wrapper doesn't clip the outside animation */
    overflow: visible !important;
}

.text-editing-progress::after {
    position: absolute;
    z-index: 10;

    /**
     * Offset to sit OUTSIDE the existing border.
     * We use a small gap (1px) or just the outline width.
     */
    top: calc(-1 * var(--outline-width) - 1px);
    right: calc(-1 * var(--outline-width) - 1px);
    bottom: calc(-1 * var(--outline-width) - 1px);
    left: calc(-1 * var(--outline-width) - 1px);

    content: "";
    pointer-events: none;
}

@keyframes label-border-dance {
    0% {
        background-position: 0 0, 100% 100%, 0 100%, 100% 0;
    }
    100% {
        background-position: 100% 0, 0 100%, 0 0, 100% 100%;
    }
}


.text-widget-editor-header {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--lgs-gutter-xs);
    gap: var(--lgs-gutter-xs);
}

.text-widget-editor-header .drawer-horizontal-line .drawer-horizontal-element:last-child {
    margin-left: auto;

}

.text-widget-editor-size-format-line {
    flex-wrap: nowrap;
}

.text-widget-editor-format-row.two-columns > * {
    flex: 1 1 0;
    min-width: 0;
}

.text-widget-editor-format-row wa-button-group {
    flex: 0 0 auto;
    white-space: nowrap;
}

.text-widget-editor-header sl-button:not(.square-button) sl-icon:first-child {
    margin-left: var(--lgs-gutter-s);
}

.text-widget-editor .text-widget-toolbar {
    display: flex;
    align-items: center;
    gap: var(--lgs-gutter-s);
}

.text-widget-editor-scroll {
    flex: 1 1 auto;
    min-height: 0;
}

.text-widget-toolbar {
    display: flex;
    align-items: center;
    gap: var(--lgs-gutter-xs);
}

.text-widget-toolbar .square-button.small::part(label) {
    font-size: var(--lgs-font-size-s);
    padding: 0;
}

/*******************************************************************************
 * Text Widget Preview Styles
 * Logic: Auto-sizing with perfect vertical and horizontal centering.
 * Fix: Box-shadow visibility on Shadow DOM.
 ******************************************************************************/

.text-widget-preview-area {
    display: grid;
    overflow: hidden;
    width: 100%;
    height: 250px;
    background: transparent;
    place-items: center;

    --wa-focus-ring-color: transparent;
    --wa-focus-ring-width: 0;
}

/* * Shoelace Base Shadow Part
 * Handles Elevation (Box Shadow) and allows shrinking
 */
.text-widget-preview-area::part(base) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: visible !important;
    width: var(--lgs-preview-content-width, auto);
    max-width: none;
    height: var(--lgs-preview-content-height, auto);

    /* Apply Custom Box Shadow */
    border: none !important;
    border-radius: var(--lgs-tx-radius);

    /* Force visibility of shadow */
    background: transparent !important;

}

/* * Layout wrapper
 * Padding is vital to prevent the box-shadow from being clipped
 */
.text-widget-preview-area::part(form-control) {
    display: inline-block;
    margin: 0;
    padding: 0;
}

/* * The Input/Textarea element
 * Core logic for auto-sizing and vertical centering
 */
.text-widget-preview-area::part(textarea) {
    font-family: var(--lgs-tx-font) !important;
    font-size: var(--lgs-tx-size);
    font-weight: var(--lgs-tx-weight);
    font-style: var(--lgs-tx-style);
    line-height: var(--lgs-tx-line-height) !important;
    display: inline-block;

    overflow: hidden;
    box-sizing: border-box;
    width: var(--lgs-preview-content-width, auto);
    min-width: 1ch;
    max-width: none;
    height: auto;
    min-height: 1em;
    margin: 0;
    padding: var(--lgs-tx-textarea-padding-top) var(--lgs-tx-padding-right) var(--lgs-tx-textarea-padding-bottom) var(--lgs-tx-padding-left);
    resize: none;

    transition: text-shadow 0.2s ease,
    background-color 0.2s ease,
    border 0.2s ease,
    box-shadow 0.2s ease;
    transform: var(--lgs-tx-transform);
    text-align: var(--lgs-tx-align);

    white-space: pre-wrap;

    word-wrap: break-word;
    color: var(--lgs-tx-color) !important;
    border: var(--lgs-tx-border);

    /* Theming & Shadows */
    border-radius: var(--lgs-tx-radius);
    background-color: var(--lgs-tx-bg-color);
    box-shadow: var(--lgs-bg-elevation);
    text-shadow: var(--lgs-tx-shadow);

    field-sizing: content;
    backdrop-filter: blur(var(--lgs-tx-blur));
    field-sizing: content;

    -webkit-text-stroke: var(--lgs-tx-stroke-color);
    -webkit-text-stroke-width: var(--lgs-tx-stroke-width);
    -webkit-text-fill-color: var(--lgs-tx-color) !important;
    paint-order: var(--lgs-tx-paint-order);

    --sl-input-color: var(--lgs-tx-color);
    --sl-input-color-hover: var(--lgs-tx-color);
    --sl-input-color-focus: var(--lgs-tx-color);

    --sl-input-border-color-focus: transparent;
}

.text-widget-preview-area::part(form-control-input) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.text-widget-rotation {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: var(--lgs-gutter-xs);
}

body.mobile .text-widget-editor-size-format-line .lgs--text-widget-font-size-trigger {
    flex: 0 0 auto;
    width: 10rem;
    min-width: 0;
}

body.mobile .text-widget-editor-format-row {
    overflow-x: auto;
    flex: 1 1 100%;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    margin-left: 0;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

body.mobile .text-widget-editor-format-row::-webkit-scrollbar {
    display: none;
}
